import type { ReportResponseDto } from '@/api/WhisApi/generated';
import {
  getReportsFindAllQueryKey,
  getReportsFindOneQueryKey,
  ReportResponseDtoStatus,
  useReportsRemove,
} from '@/api/WhisApi/generated';
import {
  AlertDialog,
  type AlertDialogAction,
} from '@/components/AlertDialog/AlertDialog';
import { Button } from '@/components/Button/Button';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import {
  canDeleteReportDraft,
  canEditReport,
  isUserAdministrator,
  isUserDelegate,
} from '@/utils/userPermissions';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useSegments } from 'expo-router';
import { XStack } from 'tamagui';
import { useRequestRra } from './rra/useRequestRra';
import { useUploadRra } from './rra/useUploadRra';

interface ReportDetailFooterActionsProps {
  reportId: string;
  report: ReportResponseDto;
}

export const ReportDetailFooterActions = ({
  reportId,
  report,
}: ReportDetailFooterActionsProps): React.ReactElement => {
  const { t } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const { user } = useAuth();
  const router = useRouter();
  const segments = useSegments() as string[];
  const isBoardRoute = segments.includes('board');
  const reportsEditBasePath = isBoardRoute
    ? '/(tabs)/board/reports'
    : '/(tabs)/reports';
  const reportsListPath = isBoardRoute ? '/(tabs)/board' : '/(tabs)/reports';
  const queryClient = useQueryClient();
  const { mutate: removeReport, isPending: isRemoving } = useReportsRemove();
  const { requestRra, isRequestingRra } = useRequestRra({
    reportId,
    isBoardRoute,
  });
  const { uploadRra, isUploadingRra } = useUploadRra({
    report,
    isBoardRoute,
  });

  const canEdit = canEditReport(report, user);
  const canRequestRra =
    isUserAdministrator(user) ||
    (isUserDelegate(user) && user?.country?.code === report.country.code);
  const canDeleteDraft = canDeleteReportDraft(report, user);

  const isDraftStatus = report.status === ReportResponseDtoStatus.draft;
  const isPendingStatus = report.status === ReportResponseDtoStatus.pendingRRA;
  const isPublishedStatus = report.status === ReportResponseDtoStatus.published;
  const editAllowedStatuses: ReportResponseDtoStatus[] = [
    ReportResponseDtoStatus.draft,
    ReportResponseDtoStatus.pendingRRA,
    ReportResponseDtoStatus.published,
    ReportResponseDtoStatus.publishedRRA,
  ];
  const showEditButton =
    canEdit &&
    report.isLastVersion &&
    editAllowedStatuses.includes(report.status);
  const showRequestRraButton =
    isPublishedStatus && report.isLastVersion && canRequestRra;
  const showUploadRraButton =
    isPendingStatus && report.isLastVersion && canRequestRra;
  const hasSecondaryAction = showRequestRraButton || showUploadRraButton;
  const hasActions =
    (isDraftStatus && canDeleteDraft) || showEditButton || hasSecondaryAction;

  const handleEdit = (): void => {
    router.push({
      pathname: `${reportsEditBasePath}/[reportId]/edit`,
      params: { reportId },
    });
  };

  const handleDelete = (): void => {
    removeReport(
      { reportId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getReportsFindAllQueryKey(),
          });
          queryClient.invalidateQueries({
            queryKey: getReportsFindOneQueryKey(reportId),
          });
          router.replace(reportsListPath);
        },
      },
    );
  };

  const deleteActions: AlertDialogAction[] = [
    {
      label: t('buttons.cancel'),
      variant: 'outlined',
      actionType: 'cancel',
    },
    {
      label: t('reportDetail.actions.delete'),
      onPress: handleDelete,
      variant: 'destructive',
      actionType: 'action',
    },
  ];

  return (
    <XStack
      gap="$3"
      paddingTop="$true"
      borderTopWidth={hasActions ? 1 : 0}
      borderTopColor="$borderColor"
      justifyContent="flex-end"
      alignItems="center"
      alignSelf="center"
      width="100%"
      maxWidth="$compactPageWidth"
    >
      {isDraftStatus && canDeleteDraft && (
        <AlertDialog
          title={t('reportDetail.deleteDialog.title')}
          description={t('reportDetail.deleteDialog.description')}
          actions={deleteActions}
        >
          <Button
            title={t('reportDetail.actions.delete')}
            variant="outlined-destructive"
            disabled={isRemoving}
            flex={isSmallScreen ? 1 : 0}
            maxWidth="50%"
          />
        </AlertDialog>
      )}
      {showEditButton && (
        <Button
          title={t('reportDetail.actions.edit')}
          variant={isDraftStatus ? 'primary' : 'outlined'}
          onPress={handleEdit}
          flex={isSmallScreen ? 1 : 0}
          maxWidth={hasSecondaryAction ? '50%' : undefined}
        />
      )}
      {showRequestRraButton && (
        <Button
          title={t('reportDetail.actions.requestRra')}
          variant="primary"
          onPress={requestRra}
          disabled={isRequestingRra}
          flex={isSmallScreen ? 1 : 0}
          maxWidth="50%"
        />
      )}
      {showUploadRraButton && (
        <Button
          title={t('reportDetail.actions.uploadRra')}
          variant="primary"
          onPress={() => void uploadRra()}
          disabled={isUploadingRra}
          flex={isSmallScreen ? 1 : 0}
          maxWidth="50%"
        />
      )}
    </XStack>
  );
};
